import { useState } from "react";
import { DocumentationTabsData } from "../constants/Frameworks";
import { useNavigate } from "react-router-dom";
import logoPrimary from "../../../assets/logos/pbxscribe-logo.svg";
const DocTabs = () => {
  const [activeTabIdx, setActiveTabIdx] = useState(0);
  const navigate = useNavigate();
  const tabs = DocumentationTabsData;
  const activeTab = tabs[activeTabIdx] || {};

  // Section headers for each tab (match image)
  const sectionHeaders = {
    gettingStarted: "Key Points:",
    smsEmail: "",
    voicemail: "Key Settings:",
    billing: "Details:",
    security: "Important Settings:",
    troubleshooting: "Common Problems & Solutions:",
  };

  // Special rendering for SMS & Email Notifications tab (second tab)
  const renderSmsEmailContent = () => (
    <>
      <div className="mb-2 text-primary">
        This section helps you manage how you receive alerts and messages.
        Proper settings ensure you never miss important updates.
      </div>
      <div className="mb-2">
        <div className="font-bold md:text-lg lg:text-xl mb-1">
          Email Notifications:
        </div>
        <ul className="list-disc ml-6 md:text-lg lg:text-xl text-gray-700">
          <li>
            Toggle ON to receive alerts such as missed calls, system updates,
            and voicemail notifications directly in your inbox.
          </li>
          <li>
            Customize frequency and type of email notifications in settings.
          </li>
          <li>Use a verified email address to avoid delivery failures.</li>
        </ul>
      </div>
      <div className="mb-2">
        <div className="font-bold md:text-lg lg:text-xl mb-1">
          SMS Notifications:
        </div>
        <ul className="list-disc ml-6 md:text-lg lg:text-xl text-gray-700">
          <li>Toggle ON/OFF to receive alerts on your mobile number.</li>
          <li>
            Useful for urgent notifications if you are away from your email.
          </li>
          <li>Ensure your mobile number is correct to avoid missed SMS.</li>
        </ul>
      </div>
      <div className="mb-2">
        <div className="font-bold md:text-lg lg:text-xl mb-1">
          System Alerts:
        </div>
        <ul className="list-disc ml-6 md:text-lg lg:text-xl text-gray-700">
          <li>
            Receive notifications about delivery failures, system maintenance,
            or updates.
          </li>
          <li>Helps you stay informed about critical issues.</li>
        </ul>
      </div>
      <div className="mb-2">
        <div className="font-bold md:text-lg lg:text-xl mb-1">Tips:</div>
        <ul className="list-disc ml-6 md:text-lg lg:text-xl text-gray-700">
          <li>Combine email and SMS notifications for redundancy.</li>
          <li>Check your spam folder if emails are not received.</li>
          <li>Adjust notification settings according to your priorities.</li>
        </ul>
      </div>
    </>
  );

  // Special rendering for Troubleshooting tab (last tab)
  const renderTroubleshootingContent = () => (
    <>
      {/* <div className="mb-2 text-primary">Common issues related to notifications, emails, and voicemail are addressed here.</div> */}
      <div className="mb-2">
        <div className="font-bold md:text-lg lg:text-xl mb-1">
          Common Problems & Solutions:
        </div>
        <ul className="list-disc ml-6 md:text-lg lg:text-xl text-gray-700">
          <li>
            <span className="font-bold">Missing Email Notifications:</span>
            <ul className="list-disc ml-6">
              <li>Ensure your email address is correct and verified.</li>
              <li>Check spam/junk folder for missed alerts.</li>
              <li>
                Toggle settings correctly for the type of notifications you
                want.
              </li>
            </ul>
          </li>
          <li>
            <span className="font-bold">SMS Alerts Not Received:</span>
            <ul className="list-disc ml-6">
              <li>Verify your mobile number in settings.</li>
              <li>Ensure your phone has network coverage.</li>
            </ul>
          </li>
          <li>
            <span className="font-bold">Voicemail Issues:</span>
            <ul className="list-disc ml-6">
              <li>Make sure “Attach Audio File” is ON.</li>
              <li>Confirm transcription language is set correctly.</li>
            </ul>
          </li>
          <li>
            <span className="font-bold">Support Options:</span>
            <ul className="list-disc ml-6">
              <li>Use the “Need Help?” card for documentation and guides.</li>
              <li>Contact customer support via email if issues persist.</li>
            </ul>
          </li>
        </ul>
      </div>
      <div className="mb-2">
        <div className="font-bold md:text-lg lg:text-xl mb-1">Tips:</div>
        <ul className="list-disc ml-6 md:text-lg lg:text-xl text-gray-700">
          <li>Keep your dashboard and app updated for the best performance.</li>
          <li>
            Regularly check notification settings to avoid missing critical
            updates.
          </li>
          <li>Backup important emails and voicemails for reference.</li>
        </ul>
      </div>
    </>
  );

  // Tab navigation handlers
  const handleNext = () => {
    if (activeTabIdx < tabs.length - 1) setActiveTabIdx(activeTabIdx + 1);
  };
  const handleBack = () => {
    if (activeTabIdx > 0) setActiveTabIdx(activeTabIdx - 1);
  };
  const handleClose = () => {
    navigate("/support");
  };

  return (
    <div className="bg-light rounded-xl shadow w-full mx-auto px-3 md:px-6 ">
      <div className="flex flex-col items-start gap-7 px-4 sm:px-6 pt-6 bg-line rounded-t-xl">
        <img src={logoPrimary} alt="Logo" className="h-8 mr-4" />
        <div className="flex overflow-x-auto w-full scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-transparent ">
          {tabs.map((tab, idx) => (
            <button
              key={tab.key}
              className={`px-2 xl:px-4 py-2 lg:text-lg xl:text-xl 1xl:text-2xl focus:outline-none transition-colors duration-150 first:rounded-tl-lg last:rounded-tr-lg border-r border-primary/30 last:border-r-0 whitespace-nowrap flex-shrink-0 ${
                activeTabIdx === idx
                  ? "bg-white text-primary/50 font-bold"
                  : "bg-primary/30 text-primary/50 font-medium"
              }`}
              onClick={() => setActiveTabIdx(idx)}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>
      <div className="bg-white rounded-b-xl p-6 pt-8 sm:pt-12 border border-t-0 md:text-lg lg:text-xl px-3 md:px-6">
        <div className="max-w-[1250px]">
          <div className="mb-4 md:text-lg lg:text-xl text-content">
            {activeTab.title}
          </div>
          {activeTab.key === "smsEmail" && renderSmsEmailContent()}
          {activeTab.key === "troubleshooting" &&
            renderTroubleshootingContent()}
          {activeTab.key !== "smsEmail" &&
            activeTab.key !== "troubleshooting" && (
              <>
                {sectionHeaders[activeTab.key] && (
                  <div className="font-bold mb-1">
                    {sectionHeaders[activeTab.key]}
                  </div>
                )}
                {Array.isArray(activeTab.keyPoints) &&
                  activeTab.keyPoints.length > 0 && (
                    <ul className="list-disc ml-6 text-content mb-2">
                      {activeTab.keyPoints.map((point, idx) => (
                        <li key={idx}>{point}</li>
                      ))}
                    </ul>
                  )}
                {Array.isArray(activeTab.tips) && activeTab.tips.length > 0 && (
                  <>
                    <div className="font-bold mb-1">Tips:</div>
                    <ul className="list-disc ml-6 text-content mb-2">
                      {activeTab.tips.map((tip, idx) => (
                        <li key={idx}>{tip}</li>
                      ))}
                    </ul>
                  </>
                )}
                {Array.isArray(activeTab.advice) &&
                  activeTab.advice.length > 0 && (
                    <>
                      <div className="font-bold mb-1">Additional Advice:</div>
                      <ul className="list-disc ml-6 text-content mb-2">
                        {activeTab.advice.map((advice, idx) => (
                          <li key={idx}>{advice}</li>
                        ))}
                      </ul>
                    </>
                  )}
              </>
            )}
        </div>
        <div className="flex justify-end mt-6 space-x-2">
          {activeTabIdx === 0 ? null : (
            <button
              className="bg-primary text-white px-5 py-2 rounded-lg font-semibold text-sm shadow hover:bg-mutedBlue transition-colors"
              onClick={handleBack}
            >
              GO BACK
            </button>
          )}
          {activeTabIdx === tabs.length - 1 ? (
            <button
              className="bg-primary text-white px-5 py-2 rounded-lg font-semibold text-sm shadow hover:bg-mutedBlue transition-colors"
              onClick={handleClose}
            >
              CLOSE
            </button>
          ) : (
            <button
              className="bg-primary text-white px-5 py-2 rounded-lg font-semibold text-sm shadow hover:bg-mutedBlue transition-colors"
              onClick={handleNext}
            >
              Next
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default DocTabs;
